import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { adminApi } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, Trash2, Save, RefreshCw, ExternalLink } from "lucide-react";
import { ApiKeyVault } from "@/components/ApiKeyVault";

export default function AdminSettings() {
  const [status, setStatus] = useState(null);
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/integrations/hubspot");
      setStatus(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!token.trim()) {
      toast.error("Please paste a HubSpot Private App token.");
      return;
    }
    setSaving(true);
    try {
      const r = await adminApi.put("/admin/integrations/hubspot", { token: token.trim() });
      setStatus(r.data);
      setToken("");
      toast.success("HubSpot connected.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save token.");
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    try {
      await adminApi.delete("/admin/integrations/hubspot");
      toast.success("Settings override removed.");
      await load();
    } catch {
      toast.error("Could not disconnect.");
    }
  };

  const syncAll = async () => {
    setSyncing(true);
    try {
      const r = await adminApi.post("/admin/integrations/hubspot/sync-all");
      toast.success(
        `Synced: ${r.data.submissions} submissions · ${r.data.investing_leads} investing · ${r.data.contacts} contacts` +
          (r.data.errors ? ` · ${r.data.errors} errors` : "")
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed.");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <main data-testid="admin-settings" className="max-w-4xl mx-auto px-6 md:px-10 py-10 lg:py-14">
      <div className="eyebrow">Studio</div>
      <h1 className="font-serif-display text-4xl lg:text-5xl mt-3 text-[#2A1F1A]">
        Integrations
      </h1>
      <p className="mt-3 text-sm text-[#6E6E70] max-w-lg">
        Connect Sasha.host to the outside world — CRM, communications and more.
      </p>

      {/* HubSpot */}
      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        data-testid="hubspot-card"
        className="mt-10 bg-white border border-[#E3DED5] p-6 lg:p-8"
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="eyebrow">CRM</div>
            <h2 className="font-serif-display text-2xl text-[#2A1F1A] mt-1">HubSpot</h2>
            <p className="text-sm text-[#6E6E70] mt-1">
              Sync every host application, investing lead and contact form as
              a HubSpot Contact. Ready for Workflows.
            </p>
          </div>
          {loading ? (
            <Loader2 size={18} className="animate-spin text-[#6E6E70]" />
          ) : status?.configured ? (
            <span
              data-testid="hubspot-connected-badge"
              className="inline-flex items-center gap-2 text-xs px-3 py-1.5 border border-[#C03028] text-[#C03028]"
            >
              <CheckCircle2 size={14} /> Connected
              {status.portal_id && (
                <span className="text-[#8E8F90] ml-1">
                  · Portal {status.portal_id}
                </span>
              )}
            </span>
          ) : (
            <span
              data-testid="hubspot-disconnected-badge"
              className="inline-flex items-center gap-2 text-xs px-3 py-1.5 border border-[#E3DED5] text-[#6E6E70]"
            >
              <XCircle size={14} /> Not connected
            </span>
          )}
        </div>

        <div className="mt-6 border-t border-[#E3DED5] pt-6">
          <Label className="eyebrow">HubSpot Private App token</Label>
          <p className="text-xs text-[#8E8F90] mt-1">
            Get one from your HubSpot workspace →{" "}
            <a
              href="https://developers.hubspot.com/docs/api/private-apps"
              target="_blank"
              rel="noreferrer"
              className="text-[#C03028] hover:underline inline-flex items-center gap-1"
            >
              docs <ExternalLink size={11} />
            </a>{" "}
            · Required scopes: <code>crm.objects.contacts.read</code>,{" "}
            <code>crm.objects.contacts.write</code>,{" "}
            <code>crm.objects.deals.write</code>.
          </p>

          {status?.configured && status?.masked_token && (
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <div className="text-sm text-[#2A1F1A] font-mono bg-[#F2EFE9] px-3 py-2 inline-block">
                {status.masked_token}
              </div>
              <span
                data-testid="hubspot-token-source"
                className="text-[11px] uppercase tracking-wide px-2 py-1 border border-[#E3DED5] text-[#6E6E70]"
              >
                {status.source === "env"
                  ? "Secure default · backend .env"
                  : "Settings override · stored securely, masked"}
              </span>
            </div>
          )}

          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <Input
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="pat-na1-..."
              data-testid="hubspot-token-input"
              className="editorial-input flex-1"
              type="password"
            />
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="hubspot-save-btn"
              className="btn-primary inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Saving…" : status?.configured ? "Update" : "Connect"}
            </button>
          </div>
        </div>

        {status?.configured && (
          <div className="mt-6 border-t border-[#E3DED5] pt-6 flex flex-col sm:flex-row gap-3 justify-between">
            <button
              type="button"
              onClick={syncAll}
              disabled={syncing}
              data-testid="hubspot-sync-btn"
              className="btn-secondary inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm"
            >
              {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              {syncing ? "Syncing…" : "Sync all existing records"}
            </button>
            <button
              type="button"
              onClick={disconnect}
              data-testid="hubspot-disconnect-btn"
              className="btn-secondary inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm text-[#C03028] border-[#C03028]"
            >
              <Trash2 size={14} /> Disconnect
            </button>
          </div>
        )}
      </motion.section>

      {/* What syncs — informational */}
      <section
        data-testid="sync-info-card"
        className="mt-8 bg-white border border-[#E3DED5] p-6 lg:p-8"
      >
        <div className="eyebrow">What Sasha pushes to HubSpot</div>
        <h2 className="font-serif-display text-2xl text-[#2A1F1A] mt-1">
          Contacts auto-populate the moment they submit
        </h2>
        <p className="text-sm text-[#6E6E70] mt-2">
          Every new record — host application, investing lead, contact form — is
          upserted to HubSpot as a Contact, ready for your Workflows.
          Properties analyzed on the <a href="/admin/scout" className="text-[#C03028] underline">Scout page</a>{" "}
          also push Sasha&apos;s Go/No-Go analysis as custom properties on the
          Contact + Deal, plus a formatted Note engagement your Breeze
          automations can pick up.
        </p>
        <ul className="mt-4 space-y-2 text-sm text-[#2A1F1A]">
          <li className="flex items-start gap-2">
            <span className="text-[#B0895C] mt-0.5">·</span>
            <div>
              <strong>Host application</strong> → Contact with{" "}
              <code className="text-xs bg-[#F2EFE9] px-1">sasha_tier</code>,{" "}
              <code className="text-xs bg-[#F2EFE9] px-1">sasha_property_name</code>,{" "}
              <code className="text-xs bg-[#F2EFE9] px-1">sasha_submission_id</code>,{" "}
              <code className="text-xs bg-[#F2EFE9] px-1">sasha_status</code>
            </div>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#B0895C] mt-0.5">·</span>
            <div>
              <strong>Investing lead</strong> → Contact with{" "}
              <code className="text-xs bg-[#F2EFE9] px-1">investing_amount_usd</code>
            </div>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#B0895C] mt-0.5">·</span>
            <div>
              <strong>Contact form message</strong> → Contact with{" "}
              <code className="text-xs bg-[#F2EFE9] px-1">sasha_last_message</code>
            </div>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#B0895C] mt-0.5">·</span>
            <div>
              <strong>Property scout Go/No-Go</strong> → Contact + Deal with{" "}
              <code className="text-xs bg-[#F2EFE9] px-1">sasha_scout_overall</code>,{" "}
              <code className="text-xs bg-[#F2EFE9] px-1">sasha_scout_lease_recommendation</code>,{" "}
              <code className="text-xs bg-[#F2EFE9] px-1">sasha_scout_owner_financing_recommendation</code>,{" "}
              <code className="text-xs bg-[#F2EFE9] px-1">sasha_scout_property_management_recommendation</code>{" "}
              (+ scores, reasons, concerns and a Note engagement)
            </div>
          </li>
        </ul>
        <p className="mt-4 text-xs text-[#8E8F90]">
          Your Breeze workflow reads these custom properties (and the Note body)
          to trigger the right communications automation.
        </p>
      </section>

      <ApiKeyVault />
    </main>
  );
}
